import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { useBrandingColors } from '../hooks/useBrandingColors';
import { useAuthStore } from '../store/authStore';
import { creditsLimitsService } from '../services/creditsLimitsService';
import { Pagination } from './vacancies/components/pagination';
import type {
  AiServiceCreditType,
  BalanceWidgetItem,
  ServiceCardItem,
  TeamMemberItem,
  PersonalServiceCard,
  UserServiceUsage,
} from '../services/creditsLimitsService';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmt(n: number): string {
  return n.toLocaleString('ru-RU');
}

function kopToRub(kopecks: number): string {
  return fmt(Math.round(kopecks / 100));
}

const SERVICE_ORDER: AiServiceCreditType[] = [
  'SEARCH_CREDITS',
  'DIALOG_CREDITS',
  'INTERVIEW_CREDITS',
];

const SERVICE_LABEL: Record<AiServiceCreditType, string> = {
  SEARCH_CREDITS: 'Скоринг резюме',
  DIALOG_CREDITS: 'Диалоги с ИИ',
  INTERVIEW_CREDITS: 'AI-Скрининг',
};

// Заголовки колонок таблицы — по макету (мн. число у скринингов)
const COLUMN_LABEL: Record<AiServiceCreditType, string> = {
  SEARCH_CREDITS: 'Скоринг резюме',
  DIALOG_CREDITS: 'Диалоги с ИИ',
  INTERVIEW_CREDITS: 'AI-Скрининги',
};

// Дефолтные тарифы в рублях за шт — по задаче 855
const DEFAULT_PRICE_RUB: Record<AiServiceCreditType, number> = {
  SEARCH_CREDITS: 1, // 1₽/шт
  DIALOG_CREDITS: 20, // 20₽/шт
  INTERVIEW_CREDITS: 80, // 80₽/шт
};

function initials(first: string, last: string): string {
  return `${(first[0] || '').toUpperCase()}${(last[0] || '').toUpperCase()}`;
}

// ─── Icons ───────────────────────────────────────────────────────────────────
// Все иконки сервисов — монохром (#161922), 14×14, по макету.

const SearchCreditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M8.17 1.17H3.5a1.17 1.17 0 0 0-1.17 1.16v9.34a1.17 1.17 0 0 0 1.17 1.16h7a1.17 1.17 0 0 0 1.17-1.16V4.67L8.17 1.17Z"
      stroke="currentColor" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round"
    />
    <path d="M8.17 1.17v2.33a1.17 1.17 0 0 0 1.16 1.17h2.34" stroke="currentColor" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M4.67 6.42h1.16M9.33 8.75H4.67M9.33 11.08H4.67" stroke="currentColor" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DialogCreditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M11.08 7.58a1.17 1.17 0 0 1-1.16 1.17H4.08L1.75 11.08V3.33a1.17 1.17 0 0 1 1.17-1.16h7a1.17 1.17 0 0 1 1.16 1.16v4.25Z"
      stroke="currentColor" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round"
    />
  </svg>
);

const InterviewCreditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M9.33 6.13l3.04 2.03a.29.29 0 0 0 .46-.24V4.08a.29.29 0 0 0-.46-.24L9.33 5.87"
      stroke="currentColor" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round"
    />
    <rect x="1.17" y="3.5" width="8.17" height="7" rx="1.17" stroke="currentColor" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SERVICE_ICON: Record<AiServiceCreditType, React.FC> = {
  SEARCH_CREDITS: SearchCreditIcon,
  DIALOG_CREDITS: DialogCreditIcon,
  INTERVIEW_CREDITS: InterviewCreditIcon,
};

// 26×26 плитка #F4F5F0, иконка прижата к верху (padding 6 6 0) — по макету.
const ServiceIconBadge: React.FC<{ type: AiServiceCreditType }> = ({ type }) => {
  const Icon = SERVICE_ICON[type];
  return (
    <div className="w-[26px] h-[26px] rounded-[6px] bg-[#F4F5F0] flex flex-col items-start pt-[6px] px-[6px] shrink-0 text-[#161922]">
      <Icon />
    </div>
  );
};

const ChevronDownIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M6 8l4 4 4-4" stroke="#868C98" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const WalletIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M9.33 6.42h2.34a.58.58 0 0 1 .58.58v1.17a.58.58 0 0 1-.58.58H9.33a1.17 1.17 0 0 1 0-2.33Z"
      stroke="#989286" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round"
    />
    <path
      d="M12.25 6.42V5.25a.58.58 0 0 0-.58-.58H2.92a.58.58 0 0 1-.59-.59c0-.32.26-.58.59-.58h8.16M2.33 4.08v6.42a.58.58 0 0 0 .59.58h8.16a.58.58 0 0 0 .59-.58V8.75"
      stroke="#989286" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round"
    />
  </svg>
);

const PencilIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className={className}>
    <path
      d="M8.5 1.5l2 2L4 10H2V8L8.5 1.5Z"
      stroke="#6B7280" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"
    />
  </svg>
);

// ─── Balance Widget ───────────────────────────────────────────────────────────

interface BalanceWidgetProps {
  /** Можно распределить = бюджет − выдано лимитами − траты без лимитов (копейки, с бэка). */
  availableToDistributeKopecks: number;
  /** «Выдано лимитами» = Σ(штучный лимит × цена), копейки. */
  reservedByLimitsKopecks: number;
  /** «Траты без лимитов» = Σ(потрачено «Общими» × цена), копейки. */
  spentByUnlimitedKopecks: number;
  /** Бюджет (серое «/ …») = баланс + всего потрачено за весь период, копейки. */
  totalToppedUpKopecks: number;
}

const BalanceWidget: React.FC<BalanceWidgetProps> = ({
  availableToDistributeKopecks,
  reservedByLimitsKopecks,
  spentByUnlimitedKopecks,
  totalToppedUpKopecks,
}) => {
  // Все числа считает бэкенд (модель макета) — UI только отображает.
  const availableKop = availableToDistributeKopecks;
  const reservedKop = reservedByLimitsKopecks;
  const spentKop = spentByUnlimitedKopecks;

  return (
    <div className="bg-white rounded-[16px] border border-[#F4F5F0] px-6 py-5 flex items-center justify-between gap-8">
      {/* Можно распределить */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <WalletIcon />
          <span className="text-[13px] font-medium text-[#989286] uppercase tracking-[0.573px]">
            Можно распределить
          </span>
        </div>
        <div className="flex items-end gap-[9px]">
          <div className="flex items-start">
            <span className="text-[48px] font-bold text-[#161922] leading-none tracking-[-0.848px]">
              {kopToRub(availableKop)}
            </span>
            <span className="text-[24px] font-semibold text-[#161922] leading-[36px] ml-[3px]">₽</span>
          </div>
          <span className="text-[13px] font-medium text-[#A8A49C] tracking-[-0.076px] mb-[3px]">
            / {kopToRub(totalToppedUpKopecks)} ₽
          </span>
        </div>
      </div>

      {/* Выдано лимитами / Траты без лимитов */}
      <div className="flex items-start gap-8 shrink-0 bg-[#FCFCFB] border border-[#F4F5F0] rounded-[12px] px-5 py-3">
        <div className="flex flex-col gap-1">
          <span className="text-[13px] font-medium text-[#6B7280] tracking-[-0.076px]">Выдано лимитами</span>
          <span className="text-[20px] font-bold text-[#161922] leading-[30px] tracking-[-0.449px]">{kopToRub(reservedKop)} ₽</span>
        </div>
        <div className="w-px self-stretch bg-[#E9EAE2]" />
        <div className="flex flex-col gap-1">
          <span className="text-[13px] font-medium text-[#6B7280] tracking-[-0.076px]">Траты без лимитов</span>
          <span className="text-[20px] font-bold text-[#161922] leading-[30px] tracking-[-0.449px]">{kopToRub(spentKop)} ₽</span>
        </div>
      </div>
    </div>
  );
};

// ─── Service Cards ────────────────────────────────────────────────────────────

interface ServiceCardsProps {
  items: ServiceCardItem[];
}

const ServiceCards: React.FC<ServiceCardsProps> = ({ items }) => {
  const sorted = SERVICE_ORDER.map(t => items.find(i => i.creditType === t)).filter(Boolean) as ServiceCardItem[];

  // Тариф за единицу: если есть фактические данные — считаем из них, иначе дефолт (Image 15)
  const getPriceRub = (item: ServiceCardItem): number => {
    if (item.totalUsedCount && item.totalUsedCount > 0 && item.totalCostKopecks > 0) {
      return Math.round(item.totalCostKopecks / item.totalUsedCount / 100);
    }
    return DEFAULT_PRICE_RUB[item.creditType] ?? 0;
  };

  return (
    <div className="grid grid-cols-3 gap-5">
      {sorted.map(item => {
        const priceRub = getPriceRub(item);
        return (
          <div key={item.creditType} className="bg-white rounded-[16px] border border-[#F4F5F0] p-5 flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <ServiceIconBadge type={item.creditType} />
                <span className="text-[14px] font-semibold text-[#161922] tracking-[-0.15px]">{SERVICE_LABEL[item.creditType]}</span>
              </div>
              <span className="flex items-center justify-center px-2 py-1 bg-[#F4F5F0] rounded-[8px] text-[12px] font-medium text-[#6B7280] shrink-0">
                {priceRub} ₽/шт
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[24px] font-semibold text-[#161922] leading-none tracking-[0.07px]">
                {kopToRub(item.totalCostKopecks)} ₽
              </span>
              <span className="text-[13px] text-[#6B7280] tracking-[-0.076px]">
                Оказано услуг: {fmt(item.totalUsedCount)} шт.
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Team Row ─────────────────────────────────────────────────────────────────

interface LimitCellProps {
  usage: { usedCount: number; creditLimit: number | null | undefined; unlimited: boolean };
  onEdit: (limit: number | null) => void;
}

const LimitCell: React.FC<LimitCellProps> = ({ usage, onEdit }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [originalValue, setOriginalValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const startEdit = () => {
    // != null защищает от undefined и null одновременно
    const initial = usage.creditLimit != null ? String(usage.creditLimit) : '';
    setValue(initial);
    setOriginalValue(initial);
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const commit = () => {
    setEditing(false);
    const trimmed = value.trim();
    // Не отправляем запрос если значение не изменилось
    if (trimmed === originalValue) return;
    if (trimmed === '') {
      onEdit(null);
    } else {
      const n = parseInt(trimmed, 10);
      if (!isNaN(n) && n >= 0) onEdit(n);
    }
  };

  if (editing) {
    return (
      <div className="flex flex-col gap-2 w-[152px]">
        <div className="flex items-center gap-2">
          <span className="text-[14px] font-medium text-[#161922] tracking-[-0.15px]">{usage.usedCount}</span>
          <span className="text-[13px] text-[#A8A49C]">/</span>
          <div className="flex items-center bg-[#E9EAE2] rounded-[4px] px-1">
            <input
              ref={inputRef}
              type="number"
              min={0}
              value={value}
              onChange={e => setValue(e.target.value)}
              onBlur={commit}
              onKeyDown={e => {
                if (e.key === 'Enter') commit();
                if (e.key === 'Escape') setEditing(false);
              }}
              placeholder="∞"
              data-testid="limit-input"
              className="w-14 bg-transparent py-px text-[14px] font-medium text-[#161922] outline-none"
            />
          </div>
          {/* Кнопка показывается только если есть что снимать (originalValue !== '') */}
          {originalValue !== '' && (
            <button
              type="button"
              title="Снять лимит — сделать Общий"
              data-testid="reset-to-unlimited"
              onMouseDown={e => {
                e.preventDefault();
                setEditing(false);
                onEdit(null);
              }}
              className="inline-flex items-center gap-0.5 bg-[#E9EAE2] hover:bg-[#dedccf] text-[#161922] text-[12px] font-medium px-1.5 py-0.5 rounded-[4px] transition-colors shrink-0"
            >
              <span>∞</span>
              <span>Общий</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  if (usage.unlimited) {
    return (
      <button onClick={startEdit} className="flex flex-col gap-2 w-[152px] text-left group">
        <div className="flex items-center gap-2">
          <span className="text-[14px] font-medium text-[#161922] tracking-[-0.15px]">{usage.usedCount}</span>
          <span className="text-[13px] text-[#A8A49C]">/</span>
          <span className="inline-flex items-center gap-1 bg-[#E9EAE2] group-hover:bg-[#dedccf] rounded-[4px] px-1.5 py-0.5 transition-colors">
            <PencilIcon />
            <span className="text-[12px] font-medium text-[#161922]">Общий</span>
          </span>
        </div>
        <div className="h-1 w-[152px] bg-[#E9EAE2] rounded-full overflow-hidden">
          <div className="h-full w-1/3 bg-[#D5D3CD] opacity-[0.74] rounded-full" />
        </div>
      </button>
    );
  }

  const limit = usage.creditLimit != null ? usage.creditLimit : 0;
  const pct = limit > 0 ? Math.min(100, Math.round((usage.usedCount / limit) * 100)) : 0;

  return (
    <button onClick={startEdit} className="flex flex-col gap-2 w-[152px] text-left group">
      <div className="flex items-center gap-2">
        <span className="text-[14px] font-medium text-[#161922] tracking-[-0.15px]">{usage.usedCount}</span>
        <span className="text-[13px] text-[#A8A49C]">/</span>
        <span className="inline-flex items-center gap-1.5 bg-[#E9EAE2] group-hover:bg-[#dedccf] rounded-[4px] px-1.5 py-0.5 transition-colors">
          <span className="text-[14px] font-medium text-[#161922]">{fmt(limit)}</span>
          <PencilIcon className="opacity-0 group-hover:opacity-100 transition-opacity" />
        </span>
      </div>
      <div className="flex items-center gap-2">
        <div className="h-1 w-[114px] bg-[#E9EAE2] rounded-full overflow-hidden">
          <div className="h-full bg-[#161922] rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-[11px] text-[#A8A49C] w-7 text-right tracking-[0.06px]">{pct}%</span>
      </div>
    </button>
  );
};

interface TeamRowProps {
  member: TeamMemberItem;
  onLimitChange: (userId: number, type: AiServiceCreditType, limit: number | null) => void;
}

const ROLE_LABEL: Record<string, string> = {
  RECRUITER: 'Рекрутёр',
  ADMIN: 'Администратор',
  OWNER: 'Владелец',
  SUPER_ADMIN: 'Супер-админ',
};

const TeamRow: React.FC<TeamRowProps> = ({ member, onLimitChange }) => {
  const fullName = `${member.firstName} ${member.lastName}`;
  const abbr = initials(member.firstName, member.lastName);
  const roleLabel = ROLE_LABEL[member.role ?? ''] ?? 'Рекрутёр';

  const getUsage = (type: AiServiceCreditType): { usedCount: number; creditLimit: number | null | undefined; unlimited: boolean } => {
    const found = member.services.find((s: UserServiceUsage) => s.creditType === type);
    return found ? { usedCount: found.usedCount, creditLimit: found.creditLimit, unlimited: found.unlimited }
      : { usedCount: 0, creditLimit: null, unlimited: true };
  };

  // Используем costKopecks из API, а не usedCount
  const totalCostKopecks = member.services.reduce(
    (sum: number, s: UserServiceUsage) => sum + (s.costKopecks || 0),
    0,
  );

  return (
    <div className="flex items-center min-h-[83px] border-b border-[#F4F5F0] last:border-b-0 hover:bg-[#FAFAFA] transition-colors">
      {/* Avatar + name + role */}
      <div className="w-[311px] shrink-0 pl-6">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-[14px] font-medium shrink-0 bg-[#F4F5F0] text-[#161922]">
            {abbr}
          </div>
          <div className="min-w-0">
            <div className="text-[15px] font-medium text-[#161922] tracking-[-0.234px] truncate">{fullName}</div>
            <div className="text-[13px] text-[#989286] tracking-[-0.076px]">{roleLabel}</div>
          </div>
        </div>
      </div>

      {/* Per-service columns */}
      {SERVICE_ORDER.map(type => (
        <div key={type} className="flex-1 pl-6 py-2">
          <LimitCell
            usage={getUsage(type)}
            onEdit={limit => onLimitChange(member.userId, type, limit)}
          />
        </div>
      ))}

      {/* Total spend — из API costKopecks */}
      <div className="w-[180px] shrink-0 text-right pr-6">
        <span className="text-[15px] font-medium text-[#161922] tracking-[-0.234px]">{kopToRub(totalCostKopecks)} ₽</span>
      </div>
    </div>
  );
};

// ─── Team Section ─────────────────────────────────────────────────────────────

interface TeamSectionProps {
  colors: ReturnType<typeof useBrandingColors>;
  onLimitSaved?: () => void;
  companyId?: number;
}

const PAGE_SIZE = 10;

const TeamSection: React.FC<TeamSectionProps> = ({ colors, onLimitSaved, companyId }) => {
  const [members, setMembers] = useState<TeamMemberItem[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sortDesc, setSortDesc] = useState(false);

  const [bulkService, setBulkService] = useState<AiServiceCreditType>('INTERVIEW_CREDITS');
  const [bulkLimit, setBulkLimit] = useState('');
  const [applying, setApplying] = useState(false);

  const searchDebounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const load = useCallback(async (page: number, q: string) => {
    setLoading(true);
    try {
      const res = await creditsLimitsService.getTeamMembers({ search: q || undefined, page: page - 1, size: PAGE_SIZE, companyId });
      setMembers(res.content ?? []);
      setTotalPages(res.totalPages || 1);
    } catch {
      toast.error('Ошибка загрузки команды');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(1, ''); }, [load]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      setSearch(val);
      setCurrentPage(1);
      load(1, val);  // eslint-disable-line react-hooks/exhaustive-deps
    }, 400);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    load(page, search);
  };

  const handleLimitChange = async (userId: number, type: AiServiceCreditType, limit: number | null) => {
    try {
      const updated = await creditsLimitsService.setUserLimit(userId, type, limit, companyId);
      setMembers((prev: TeamMemberItem[]) => prev.map((m: TeamMemberItem) => {
        if (m.userId !== userId) return m;
        return {
          ...m,
          services: m.services.map((s: UserServiceUsage) =>
            s.creditType === type
              ? { ...s, creditLimit: updated.creditLimit, unlimited: updated.unlimited }
              : s,
          ),
        };
      }));
      toast.success('Лимит обновлён');
      onLimitSaved?.();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Ошибка обновления лимита';
      toast.error(msg);
    }
  };

  const handleBulkApply = async () => {
    if (!members.length) return;
    const limitVal = bulkLimit.trim() === '' ? null : parseInt(bulkLimit, 10);
    if (bulkLimit.trim() !== '' && (isNaN(limitVal as number) || (limitVal as number) < 0)) {
      toast.error('Некорректный лимит');
      return;
    }
    setApplying(true);
    try {
      const userIds = members.map(m => m.userId);
      await creditsLimitsService.batchSetLimits(userIds, bulkService, limitVal, companyId);
      toast.success('Лимиты установлены');
      load(currentPage, search);
      onLimitSaved?.();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Ошибка применения лимитов';
      toast.error(msg);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-[18px] font-semibold text-[#161922] tracking-[-0.015em]">Управление командой</h2>

      {/* Controls */}
      <div className="flex items-center justify-between gap-8 flex-wrap">
        {/* Search */}
        <div className="flex items-center gap-2 bg-white rounded-[10px] px-2 h-10 w-[320px]">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="shrink-0">
            <circle cx="9" cy="9" r="6.5" stroke="#525866" strokeWidth="1.5"/>
            <path d="M14 14l3.5 3.5" stroke="#525866" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            placeholder="Поиск сотрудника"
            value={searchInput}
            onChange={handleSearchChange}
            className="flex-1 bg-transparent text-[14px] text-[#525866] placeholder:text-[#525866] outline-none tracking-[-0.006em]"
          />
        </div>

        {/* Right group: service dropdown + limit input + apply */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {/* Service type dropdown */}
            <div className="relative">
              <select
                value={bulkService}
                onChange={e => setBulkService(e.target.value as AiServiceCreditType)}
                className="appearance-none bg-white rounded-[10px] h-10 pl-3 pr-9 text-[14px] text-[#525866] outline-none cursor-pointer min-w-[181px] tracking-[-0.006em]"
              >
                {SERVICE_ORDER.map(t => (
                  <option key={t} value={t}>{SERVICE_LABEL[t]}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2">
                <ChevronDownIcon />
              </div>
            </div>

            {/* Limit input */}
            <input
              type="number"
              min={0}
              placeholder="100"
              value={bulkLimit}
              onChange={e => setBulkLimit(e.target.value)}
              data-testid="bulk-limit-input"
              title="Лимит (шт). Оставьте пустым чтобы снять лимит (Общий)"
              className="w-20 h-10 bg-white rounded-[10px] px-3 text-[14px] text-[#161922] placeholder:text-[#161922] outline-none tracking-[-0.006em]"
            />
          </div>

          {/* Apply button */}
          <button
            onClick={handleBulkApply}
            disabled={applying || !members.length}
            title="Применить лимит ко всем сотрудникам на текущей странице"
            className="flex items-center justify-center gap-1 px-2.5 h-10 rounded-[10px] text-white text-[14px] font-medium transition-opacity disabled:opacity-50 shrink-0"
            style={{ backgroundColor: colors.primary, boxShadow: '0px 1px 2px rgba(55,93,251,0.08)' }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M5 10.5l3.5 3.5L15 6.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="px-1">Применить ко всем</span>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-[16px] border border-[#F4F5F0] overflow-hidden">
        {/* Header */}
        <div className="flex items-center h-[52px] border-b border-[#F4F5F0]">
          <div className="w-[311px] shrink-0 pl-6 text-[13px] font-semibold text-[#6B7280] uppercase tracking-[0.573px]">
            Сотрудник
          </div>
          {SERVICE_ORDER.map(type => (
            <div key={type} className="flex-1 pl-6 text-[13px] font-semibold text-[#6B7280] uppercase tracking-[0.573px]">
              {COLUMN_LABEL[type]}
            </div>
          ))}
          <button
            onClick={() => setSortDesc(v => !v)}
            title="Сортировка по текущей странице"
            className="w-[180px] shrink-0 pr-6 text-[13px] font-semibold text-[#6B7280] uppercase tracking-[0.573px] flex items-center justify-end gap-1.5 hover:text-[#161922] transition-colors"
          >
            Траты (₽)
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="shrink-0">
              <path d="M3.5 9.33l1.75 1.75M5.25 11.08V2.92M10.5 4.67L8.75 2.92 7 4.67M8.75 2.92v8.16" stroke="#A8A49C" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Rows */}
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-7 w-7 border-b-2" style={{ borderColor: colors.primary }} />
          </div>
        ) : members.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-[#989286]">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" className="mb-3 opacity-40">
              <circle cx="20" cy="14" r="6" stroke="currentColor" strokeWidth="2"/>
              <path d="M6 36c0-7.732 6.268-14 14-14s14 6.268 14 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span className="text-[13px]">Сотрудники не найдены</span>
          </div>
        ) : (
          [...members]
            .sort((a, b) => {
              const ca = a.services.reduce(
                (s: number, x: UserServiceUsage) => s + (x.costKopecks || 0),
                0,
              );
              const cb = b.services.reduce(
                (s: number, x: UserServiceUsage) => s + (x.costKopecks || 0),
                0,
              );
              return sortDesc ? cb - ca : ca - cb;
            })
            .map(m => (
              <TeamRow key={m.userId} member={m} onLimitChange={handleLimitChange} />
            ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

// Статичная метка периода как в дизайне — backend не поддерживает фильтрацию по дате
const PeriodLabel: React.FC = () => (
  <div className="flex items-center gap-2 bg-white rounded-[10px] pl-3 pr-2.5 h-10 text-[14px] text-[#525866] select-none cursor-default tracking-[-0.006em]">
    <span>За месяц</span>
    <ChevronDownIcon />
  </div>
);

// ─── Admin View ───────────────────────────────────────────────────────────────

const AdminCreditsView: React.FC<{ companyId?: number }> = ({ companyId }) => {
  const colors = useBrandingColors();
  const [balanceItems, setBalanceItems] = useState<BalanceWidgetItem[]>([]);
  // Верхний блок (модель макета) — числа с бэка.
  const [balanceTotals, setBalanceTotals] = useState({
    availableToDistributeKopecks: 0,
    reservedByLimitsKopecks: 0,
    spentByUnlimitedKopecks: 0,
    totalToppedUpKopecks: 0,
  });
  const [serviceItems, setServiceItems] = useState<ServiceCardItem[]>([]);
  const [loading, setLoading] = useState(true);

  const applyBalance = (bal: any) => {
    setBalanceItems(bal.services ?? []);
    setBalanceTotals({
      availableToDistributeKopecks: bal.availableToDistributeKopecks ?? 0,
      reservedByLimitsKopecks: bal.reservedByLimitsKopecks ?? 0,
      spentByUnlimitedKopecks: bal.spentByUnlimitedKopecks ?? 0,
      totalToppedUpKopecks: bal.totalToppedUpKopecks ?? 0,
    });
  };

  const refetchBalance = useCallback(async () => {
    try {
      const bal = await creditsLimitsService.getBalanceWidget(companyId);
      applyBalance(bal);
    } catch { /* тихо, не критично */ }
  }, [companyId]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Все три запроса параллельно
        const [bal, svc] = await Promise.all([
          creditsLimitsService.getBalanceWidget(companyId),
          creditsLimitsService.getServiceCards(companyId),
        ]);
        applyBalance(bal);
        setServiceItems(svc.services ?? []);
      } catch {
        toast.error('Ошибка загрузки данных');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [companyId]);

  return (
    <div className="h-full overflow-y-auto">
      <div className="w-full max-w-[1224px] mx-auto px-6 py-8 flex flex-col gap-11">
        {/* Верхний блок: заголовок + баланс + карточки услуг */}
        <div className="flex flex-col gap-5">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-0.5">
              <h1 className="text-[24px] font-medium text-[#161922] tracking-[-0.015em]">Управление лимитами команды</h1>
              <p className="text-[12px] font-medium text-[#6B7280]">
                Распределение единого баланса и статистика использования ИИ-сервисов
              </p>
            </div>
            <PeriodLabel />
          </div>

          {/* Balance + Service cards — с собственным индикатором загрузки */}
          {loading ? (
            <div className="flex flex-col gap-5">
              <div className="rounded-[16px] border border-[#F4F5F0] h-[122px] animate-pulse bg-[#f6f8fa]" />
              <div className="grid grid-cols-3 gap-5">
                {[0, 1, 2].map(i => (
                  <div key={i} className="rounded-[16px] border border-[#F4F5F0] h-[132px] animate-pulse bg-[#f6f8fa]" />
                ))}
              </div>
            </div>
          ) : (
            <>
              {balanceItems.length > 0 && (
                <BalanceWidget
                  availableToDistributeKopecks={balanceTotals.availableToDistributeKopecks}
                  reservedByLimitsKopecks={balanceTotals.reservedByLimitsKopecks}
                  spentByUnlimitedKopecks={balanceTotals.spentByUnlimitedKopecks}
                  totalToppedUpKopecks={balanceTotals.totalToppedUpKopecks}
                />
              )}
              {serviceItems.length > 0 && <ServiceCards items={serviceItems} />}
            </>
          )}
        </div>

        {/* Таблица команды загружается параллельно — не ждёт balance/services */}
        <TeamSection colors={colors} onLimitSaved={refetchBalance} companyId={companyId} />
      </div>
    </div>
  );
};

// ─── Recruiter View ───────────────────────────────────────────────────────────

const RecruiterCreditsView: React.FC = () => {
  const colors = useBrandingColors();
  const [cards, setCards] = useState<PersonalServiceCard[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    creditsLimitsService
      .getMyCredits()
      .then(res => setCards(res.services ?? []))
      .catch(() => toast.error('Ошибка загрузки квот'))
      .finally(() => setLoading(false));
  }, []);

  const pricePerUnit = (card: PersonalServiceCard): string => {
    if (!card.usedCount) return '—';
    const kopPerUnit = card.costKopecks / card.usedCount;
    return `${fmt(Math.round(kopPerUnit / 100))} ₽/шт`;
  };

  const sorted = SERVICE_ORDER.map(t => cards.find(c => c.creditType === t)).filter(Boolean) as PersonalServiceCard[];

  return (
    <div className="h-full overflow-y-auto">
      <div className="w-full max-w-[1224px] mx-auto px-6 py-8 flex flex-col gap-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-0.5">
            <h1 className="text-[24px] font-medium text-[#161922] tracking-[-0.015em]">Мои квоты</h1>
            <p className="text-[12px] font-medium text-[#6B7280]">
              Статистика использования и расходы по ИИ-сервисам
            </p>
          </div>
          <PeriodLabel />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-9 w-9 border-b-2" style={{ borderColor: colors.primary }} />
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-5">
            {sorted.map(card => {
              return (
                <div key={card.creditType} className="bg-white rounded-[16px] border border-[#F4F5F0] p-5 flex flex-col gap-4">
                  {/* Card header */}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <ServiceIconBadge type={card.creditType} />
                      <span className="text-[14px] font-semibold text-[#161922] tracking-[-0.15px]">{SERVICE_LABEL[card.creditType]}</span>
                    </div>
                    <span className="flex items-center justify-center px-2 py-1 bg-[#F4F5F0] rounded-[8px] text-[12px] font-medium text-[#6B7280] shrink-0">
                      {pricePerUnit(card)}
                    </span>
                  </div>

                  {/* Расход в рублях (REL-229: деньги, не штуки) + счётчик операций */}
                  <div className="flex flex-col gap-1">
                    <span className="text-[24px] font-semibold text-[#161922] leading-none tracking-[0.07px]">
                      {kopToRub(card.costKopecks)} ₽
                    </span>
                    <span className="text-[13px] text-[#6B7280] tracking-[-0.076px]">
                      Оказано услуг: {fmt(card.usedCount)} шт.
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Root export ─────────────────────────────────────────────────────────────

export const CreditsLimitsContent: React.FC<{ companyId?: number }> = ({ companyId }) => {
  const { role, user } = useAuthStore();
  const normalizedRole = String((user as any)?.role || role || '').toUpperCase();
  const isAdminOrAbove =
    normalizedRole === 'OWNER' ||
    normalizedRole === 'ADMIN' ||
    normalizedRole === 'SUPER_ADMIN';

  return (
    <div className="bg-[#f5f6f1] relative rounded-[24px] shrink-0 w-full h-full overflow-hidden">
      {isAdminOrAbove ? <AdminCreditsView companyId={companyId} /> : <RecruiterCreditsView />}
    </div>
  );
};
